import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown, Zap, Trophy, Flame } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from 'recharts';
import { getTrendAnalytics } from '../../lib/analytics';
import { SectionHeader, Card, ChartTooltip, MetricTile, Insight, CHART_COLORS } from './shared';
import PatchAnnotations from './PatchAnnotations';

const ComparisonCard = ({ label, first, second, unit = '', higher = true }) => {
  const diff = second - first;
  const isPositive = higher ? diff > 0 : diff < 0;
  return (
    <div className="border border-zinc-800 bg-zinc-950 p-4">
      <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase font-semibold mb-3">{label}</div>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] text-zinc-600 font-mono">1ère moitié</div>
          <div className="text-xl font-mono text-zinc-400">{first}{unit}</div>
        </div>
        <div className="flex items-center gap-1">
          {diff !== 0 && (isPositive ? <TrendingUp size={16} className="text-cyan-400" /> : <TrendingDown size={16} className="text-rose-400" />)}
        </div>
        <div className="text-right">
          <div className="text-[10px] text-zinc-600 font-mono">2e moitié</div>
          <div className={`text-xl font-mono font-bold ${isPositive ? 'text-cyan-400' : diff === 0 ? 'text-zinc-400' : 'text-rose-400'}`}>{second}{unit}</div>
        </div>
      </div>
      {diff !== 0 && (
        <div className={`text-[10px] font-mono mt-2 text-center ${isPositive ? 'text-cyan-400' : 'text-rose-400'}`}>
          {diff > 0 ? '+' : ''}{diff.toFixed(1)}{unit} ({isPositive ? 'amélioration' : 'régression'})
        </div>
      )}
    </div>
  );
};

export default function TrendsSection({ games }) {
  const data = useMemo(() => getTrendAnalytics(games), [games]);

  if (games.length < 5) {
    return (
      <div className="border border-dashed border-zinc-800 p-8 text-center">
        <div className="text-zinc-500 font-mono text-sm">Minimum 5 games nécessaires pour analyser les tendances</div>
        <div className="text-zinc-600 text-xs mt-1">Plus le bloc avance, plus les patterns se révèlent</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPIs streaks */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Streak Actuelle" value={`${data.currentStreak.count}${data.currentStreak.type || ''}`} color={data.currentStreak.type === 'W' ? 'cyan' : 'rose'} subtext={data.currentStreak.type === 'W' ? 'victoires consécutives' : 'défaites consécutives'} />
        <MetricTile label="Meilleure Série W" value={data.longestWinStreak} color="cyan" />
        <MetricTile label="Pire Série L" value={data.longestLoseStreak} color="rose" />
        <MetricTile label="Games Jouées" value={games.length} subtext={`${Math.round(games.length)}% du bloc`} color="fuchsia" />
      </div>

      {/* Insights auto */}
      <div className="space-y-2">
        {data.secondHalf.winrate > data.firstHalf.winrate + 5 && games.length >= 20 && (
          <Insight variant="success">
            <strong>📈 Tu progresses</strong> — Winrate +{data.secondHalf.winrate - data.firstHalf.winrate}% sur la 2e moitié du bloc. Continue ce qui marche.
          </Insight>
        )}
        {data.secondHalf.winrate < data.firstHalf.winrate - 5 && games.length >= 20 && (
          <Insight variant="warning">
            <strong>📉 Baisse de régime</strong> — Winrate -{data.firstHalf.winrate - data.secondHalf.winrate}% sur la 2e moitié. Fatigue, méta ? Time pour review approfondie.
          </Insight>
        )}
        {data.longestLoseStreak >= 4 && (
          <Insight variant="danger">
            <strong>⚠️ Pattern tilt</strong> — Série de {data.longestLoseStreak} défaites consécutives détectée. Rappel : 2 loses = stop pour la journée.
          </Insight>
        )}
        {data.secondHalf.avgDeaths < data.firstHalf.avgDeaths - 0.5 && games.length >= 20 && (
          <Insight variant="success">
            <strong>🎯 Discipline en hausse</strong> — Tu meurs {(data.firstHalf.avgDeaths - data.secondHalf.avgDeaths).toFixed(1)} fois de moins par game sur la 2e moitié.
          </Insight>
        )}
      </div>

      {/* Courbes rolling */}
      <Card>
        <SectionHeader title="Winrate Rolling (10 games)" subtitle="Fenêtre glissante sur 10 games — ligne 54% = target" icon={Trophy} />
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data.trends}>
            <defs>
              <linearGradient id="wrGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={CHART_COLORS.cyan} stopOpacity={0.4} />
                <stop offset="100%" stopColor={CHART_COLORS.cyan} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="gameNumber" stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} />
            <YAxis stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} domain={[0, 100]} />
            <Tooltip content={<ChartTooltip />} />
            <ReferenceLine y={54} stroke={CHART_COLORS.fuchsia} strokeDasharray="4 4" label={{ value: 'Target 54%', fill: CHART_COLORS.fuchsia, fontSize: 10, fontFamily: 'monospace' }} />
            <Area type="monotone" dataKey="winrate" stroke={CHART_COLORS.cyan} strokeWidth={2} fill="url(#wrGrad)" name="Winrate %" />
          </AreaChart>
        </ResponsiveContainer>
        <PatchAnnotations games={games} />
      </Card>

      {/* Morts + clean ratio */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <SectionHeader title="Morts / Game Rolling" subtitle="Target ≤ 4 morts/game" icon={Flame} />
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="gameNumber" stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} />
              <YAxis stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} />
              <Tooltip content={<ChartTooltip />} />
              <ReferenceLine y={4} stroke={CHART_COLORS.emerald} strokeDasharray="4 4" />
              <Line type="monotone" dataKey="avgDeaths" stroke={CHART_COLORS.rose} strokeWidth={2} dot={false} name="Morts/Game" />
            </LineChart>
          </ResponsiveContainer>
          <PatchAnnotations games={games} />
        </Card>

        <Card>
          <SectionHeader title="% Games Clean Rolling" subtitle="Games sans mort greed — target 70%+" icon={Zap} />
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="gameNumber" stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} />
              <YAxis stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} domain={[0, 100]} />
              <Tooltip content={<ChartTooltip />} />
              <ReferenceLine y={70} stroke={CHART_COLORS.fuchsia} strokeDasharray="4 4" />
              <Line type="monotone" dataKey="cleanRatio" stroke={CHART_COLORS.emerald} strokeWidth={2} dot={false} name="% Clean" />
            </LineChart>
          </ResponsiveContainer>
          <PatchAnnotations games={games} />
        </Card>
      </div>

      {/* Comparaison moitiés */}
      {games.length >= 10 && (
        <Card>
          <SectionHeader title="1ère Moitié vs 2e Moitié" subtitle="Est-ce que tu progresses dans le bloc ?" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ComparisonCard label="Winrate" first={data.firstHalf.winrate} second={data.secondHalf.winrate} unit="%" higher={true} />
            <ComparisonCard label="Morts / Game" first={data.firstHalf.avgDeaths} second={data.secondHalf.avgDeaths} higher={false} />
            <ComparisonCard label="% Games Clean" first={data.firstHalf.cleanRatio} second={data.secondHalf.cleanRatio} unit="%" higher={true} />
          </div>
        </Card>
      )}
    </div>
  );
}
